
import { mapState } from 'vuex';
import Overlay from '@/components/Overlay.vue';
import { INSERT_NOTE, INSERT_ACTION_INVOICE } from '@/gql';
import TranslationHelper from '@/helpers/translation';
import axios from 'axios';
import Contacts from '@/components/_Contacts/index.vue';
import * as CONSTANTS from '@/constants';
import { ActionsAddDocument, ActionsAddMutationVariables, Risolto_Action_Execution_Type_Enum, Risolto_Action_Source_Enum, Risolto_Action_Status_Enum, Risolto_Channel, Risolto_Channel_Enum, Risolto_Contact, Risolto_Note_Category_Enum } from '@/generated/graphql';
import Logging from '@/logging';
import { Call_Metadata } from '@/schema/channel';
import { Action_Metadata } from '@/schema/action';

export default {
	components: {
		Overlay,
		Contacts
	},
	data() {
		return {
			resultCategories: undefined,
			isDirty: false,
			loading: false,
			selectedContact: null,
			form: {
				reason_for_call: null,
				callResult: null,
				callResultDescription: ''
			},
			rules: {
				reason_for_call: [{ required: true, message: this.$t('fieldRequired') }],
				callResult: [{ required: true, message: this.$t('fieldRequired') }]
			}
		};
	},
	computed: {
		...mapState({
			forceSend: (state: any) => state.risolto.forceSendStore,
			visible: (state: any) => state.actions.createPhoneActionOverlayVisible,
			invoices: (state: any) => state.actions.createPhoneActionOverlayInvoices,
			recipient: (state: any) => state.actions.createPhoneActionOverlayRecipient,
			loggedInUser: (state: any) => state.risolto.loggedInUser
		}),
		primaryInvoice() {
			if (!this.invoices || this.invoices.length === 0) {
				return null;
			}
			const oldestInvoice = this.invoices.reduce((acc, current) => {
				if (current.issue_date < acc.issue_date) {
					acc = current;
				}

				return acc;
			});
			return oldestInvoice;
		},
		recipientNameAndAccountId() {
			if (!this.recipient) {
				return undefined;
			}

			let value = this.recipient.name;
			if (this.recipient.account_id) {
				value += ' (' + this.recipient.account_id + ')';
			}

			return value;
		},
		CONSTANTS() {
			return CONSTANTS;
		}
	},
	watch: {
		visible() {
			this.loading = false;
			this.form = {
				reason_for_call: '',
				callResult: undefined,
				callResultDescription: ''
			};
		}
	},
	created() {
		this.resultCategories = Risolto_Note_Category_Enum;
	},
	methods: {
		handleInput() {
			this.isDirty = true;
		},
		disabledContacts(contact) {
			const endpoints = contact.active_endpoints.filter((endpoint) => {
				return endpoint.channel === Risolto_Channel_Enum.Phone;
			});
			if (endpoints.length === 0) {
				return true;
			}

			return false;
		},
		handleContactSelect(contact) {
			this.selectedContact = contact;
		},
		handleOverlayClose() {
			if (this.isDirty) {
				this.$confirm(this.$t('sureClose'), this.$t('warning'), {
					confirmButtonText: 'Sluiten',
					cancelButtonText: this.$t('cancel'),
					type: 'warning'
				})
					.then(() => {
						this.isDirty = false;
						this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_VISIBLE', false);
					})
					.catch((e) => {
						Logging.error('Error in CreatePhoneActionOverlay handleClose()', e);
					});
			} else {
				this.isDirty = false;
				this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_VISIBLE', false);
			}
		},
		translateNoteCategory(cat) {
			return TranslationHelper.note_category(this, cat);
		},
		async handleResolve() {
			this.$refs['form'].validate(async (valid) => {
				if (valid) {
					await this.resolve();
				} else {
					this.loading = false;
					this.$notify({
						title: this.$t('warning'),
						message: this.$t('fillInFields'),
						type: 'warning'
					});
					return false;
				}
			});
		},
		async resolve() {
			if (this.loading) {
				return;
			}
			this.loading = true;

			try {
				this.createAndExecuteAction();
			} catch (e) {
				Logging.error(e);
			} finally {
				this.loading = false;
			}
		},
		createAndExecuteAction() {
			const recipient = this.recipient.reference;
			const participant = this.recipient.participant;
			const legalEntity = this.recipient.legal_entity;
			const channelMetadata: Call_Metadata = {
				channel: Risolto_Channel_Enum.Phone,
				recipient_name: this.recipient.name ? this.recipient.name : 'Unavailable',
				contact_first_name: this.selectedContact ? this.selectedContact['first_name'] : 'Unavailable',
				contact_last_name: this.selectedContact ? this.selectedContact['last_name'] : 'Unavailable',
				to: 'In development',
				reason_for_call: {
					message_code: this.form.reason_for_call,
					default_message: this.form.reason_for_call,
					message_parameters: {}
				},
				attachments: [],
				due_date_offset_hours: 24
			};
			const actionMetadata: Action_Metadata = {
				channels: [channelMetadata],
				attachments: [],
				allowed_channels: [Risolto_Channel_Enum.Phone],
				ignored: {},
				failed: {}
			};
			const oldestInvoice = this.invoices.reduce((acc, current) => {
				if (current.issue_date < acc.issue_date) {
					acc = current;
				}

				return acc;
			});
			const mutationVars: ActionsAddMutationVariables = {
				participant: participant,
				legalEntity: legalEntity,
				recipient: recipient,
				channel: Risolto_Channel_Enum.Phone,
				executionType: Risolto_Action_Execution_Type_Enum.Manual,
				metadata: actionMetadata,
				startTime: new Date(),
				exectutedAt: new Date(),
				status: Risolto_Action_Status_Enum.Open,
				messageCode: 'manual_phone_action',
				source: Risolto_Action_Source_Enum.Manual,
				primaryInvoice: oldestInvoice.reference,
				invoiceGroupInvoices: this.invoices.map((invoice) => {
					return {
						invoice: invoice.reference,
						participant: participant,
						legal_entity: legalEntity,
						recipient: recipient
					};
				})
			};
			Logging.log('mutationVars: ', mutationVars);
			this.$apollo
				.mutate({
					mutation: ActionsAddDocument,
					variables: mutationVars
				})
				.then(({ data }) => {
					const actionReference = data.insert_risolto_action.returning[0].reference;
					// in callback of mutation, confirm the action
					const payload = {
						version: 'v1',
						participant: this.recipient.participant,
						actions: [actionReference],
						dry_run: false,
						force_send: this.forceSend
					};
					// Add note
					let newNote = {
						note: this.form['callResultDescription'] ? this.form['callResultDescription'] : '',
						category: this.form['callResult'] ? this.form['callResult'] : undefined,
						participant: participant,
						legal_entity: legalEntity,
						recipient: recipient,
						created_by: this.loggedInUser.reference,
						action: actionReference
					};
					Logging.log('newNote: ', newNote);
					this.$apollo.mutate({
						mutation: INSERT_NOTE,
						variables: newNote
					})
						.catch(e => {
							Logging.error(e);
						});

					Logging.log('payload voor execute action: ', payload);
					axios
						.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/confirm`, payload, {
							headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
						})
						.then(() => {
							this.$notify({
								title: this.$t('success'),
								message: 'Telefonische actie',
								type: 'success'
							});

							this.isDirty = false;
							this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_VISIBLE', false);
							this.$store.commit('apollo/RISOLTO_ACTION');
						})
						.catch(e => {
							Logging.error(e);
							this.$notify({
								title: this.$t('somethingWentWrong'),
								message: this.$t('somethingWentWrong'),
								type: 'error'
							});
						});
				})
				.catch(e => {
					Logging.error(e);
					this.$notify({
						title: this.$t('somethingWentWrong'),
						message: this.$t('somethingWentWrong'),
						type: 'error'
					});
				});
		}
	}
};
